import { useState, useCallback } from 'react';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';
import {
  usageAnalyticsService,
  UsageSummary,
  OrganizationUsage,
  TopConsumer,
  TrendData,
} from '../../services/platform-admin/usage-analytics.service';

export function useUsageAnalytics() {
  const token = usePlatformAdminAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getSummary = useCallback(async (): Promise<UsageSummary | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const summary = await usageAnalyticsService.getSummary(token);
      return summary;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch summary');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const getOrganizationUsage = useCallback(
    async (
      organizationId: string,
      startDate?: string,
      endDate?: string
    ): Promise<OrganizationUsage | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const usage = await usageAnalyticsService.getOrganizationUsage(
          token,
          organizationId,
          startDate,
          endDate
        );
        return usage;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch organization usage');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getTopConsumers = useCallback(
    async (metric?: string): Promise<TopConsumer[] | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const consumers = await usageAnalyticsService.getTopConsumers(token, metric);
        return consumers;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch top consumers');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getTrends = useCallback(
    async (
      metric: string,
      startDate?: string,
      endDate?: string
    ): Promise<TrendData[] | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const trends = await usageAnalyticsService.getTrends(token, metric, startDate, endDate);
        return trends;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch trends');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return {
    isLoading,
    error,
    getSummary,
    getOrganizationUsage,
    getTopConsumers,
    getTrends,
  };
}
